import React, { useState } from 'react';
import { Filter, X, ChevronDown, Star, MapPin, Award, SortAsc, SortDesc, UserPlus } from 'lucide-react';
import { Input } from '../../../../../components/ui/input';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../../../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';

export const SupplierFilterBar = ({
  searchText,
  onSearchChange,
  selectedCountry,
  onCountryChange,
  minRating,
  onRatingChange,
  sortBy,
  onSortChange,
  sortOrder,
  onSortOrderChange,
  countries = [],
  activeFiltersCount = 0,
  onClearFilters,
  showFavoritesOnly,
  onToggleFavoritesOnly,
  handleAddSupplier
}) => {
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const sortOptions = [
    { value: 'name', label: 'Nombre' },
    { value: 'rating', label: 'Calificación' },
    { value: 'country', label: 'País' },
    { value: 'capabilities', label: 'Capacidades' },
    { value: 'leadtime', label: 'Tiempo de entrega' }
  ];

  return (
    <div>
      {/* Main search and filter row */}
      <div className="flex items-center space-x-4">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Input
            type="text"
            placeholder="Buscar proveedores por nombre, email o país..."
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-4 border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20 rounded bg-white dark:bg-slate-800"
          />
        </div>

        {/* Favorites Toggle */}
        <Button
          variant={showFavoritesOnly ? "default" : "outline"}
          size="sm"
          onClick={onToggleFavoritesOnly}
          className={`flex items-center space-x-2 transition-all duration-200 ${
            showFavoritesOnly 
              ? "bg-orange-500 hover:bg-orange-600 text-white shadow-md" 
              : "bg-white dark:bg-slate-800 border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20"
          }`}
        >
          <Star className={`h-4 w-4 ${showFavoritesOnly ? 'fill-current' : ''}`} />
          <span>Favoritos</span>
        </Button>

        {/* Filter Toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          className="bg-white dark:bg-slate-800 flex items-center space-x-2 border-slate-200/60 dark:border-slate-700/60 hover:border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200"
        >
          <Filter className="h-4 w-4" />
          <span>Filtros</span>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
              {activeFiltersCount}
            </Badge>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${isFiltersExpanded ? 'rotate-180' : ''}`} />
        </Button>

        {/* Sort Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="bg-white dark:bg-slate-800   flex items-center space-x-2 border-slate-200/60 dark:border-slate-700/60 hover:border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200">
              {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
              <span>Ordenar</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-white dark:bg-slate-800 border-slate-200/60 dark:border-slate-700/60">
            <DropdownMenuLabel className="text-slate-700 dark:text-slate-300">Ordenar por</DropdownMenuLabel>
            {sortOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => onSortChange(option.value)}
                className={`${sortBy === option.value ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300' : 'hover:bg-slate-50 dark:hover:bg-slate-700'} transition-colors`}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-slate-700 dark:text-slate-300">Orden</DropdownMenuLabel>
            <DropdownMenuItem 
              onClick={() => onSortOrderChange('asc')}
              className={`${sortOrder === 'asc' ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300' : 'hover:bg-slate-50 dark:hover:bg-slate-700'} transition-colors`}
            >
              <SortAsc className="h-4 w-4 mr-2" />
              Ascendente
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onSortOrderChange('desc')}
              className={`${sortOrder === 'desc' ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300' : 'hover:bg-slate-50 dark:hover:bg-slate-700'} transition-colors`}
            >
              <SortDesc className="h-4 w-4 mr-2" />
              Descendente
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Add Supplier Button */}
        <Button
          onClick={handleAddSupplier} 
          className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white shadow-sm"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Supplier
        </Button>

        {/* Clear Filters */}
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-slate-500 hover:text-orange-600 hover:bg-orange-50 dark:text-slate-400 dark:hover:text-orange-400 dark:hover:bg-orange-900/20 transition-all duration-200"
          >
            <X className="h-4 w-4 mr-1" />
            Limpiar
          </Button>
        )}
      </div>

      {/* Expanded Filters */}
      {isFiltersExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-200/60 dark:border-slate-700/60">
          {/* Country Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-orange-500" />
              País
            </label>
            <Select value={selectedCountry || ""} onValueChange={onCountryChange}>
              <SelectTrigger className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20">
                <SelectValue placeholder="Todos los países" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todos los países</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rating Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
              <Star className="h-4 w-4 mr-2 text-orange-500" />
              Calificación mínima
            </label>
            <Select value={minRating?.toString() || ""} onValueChange={(value) => onRatingChange(value ? parseFloat(value) : null)}>
              <SelectTrigger className="border-slate-200/60 dark:border-slate-700/60 focus:border-orange-400 focus:ring-orange-400/20">
                <SelectValue placeholder="Cualquier calificación" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Cualquier calificación</SelectItem>
                <SelectItem value="4.0">4.0+ estrellas</SelectItem>
                <SelectItem value="3.5">3.5+ estrellas</SelectItem>
                <SelectItem value="3.0">3.0+ estrellas</SelectItem>
                <SelectItem value="2.5">2.5+ estrellas</SelectItem>
                <SelectItem value="2.0">2.0+ estrellas</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quick Actions */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
              <Award className="h-4 w-4 mr-2 text-orange-500" />
              Acciones rápidas
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onToggleFavoritesOnly(!showFavoritesOnly)}
                className={`text-xs transition-all duration-200 ${
                  showFavoritesOnly 
                    ? 'bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-900/20 dark:border-orange-700 dark:text-orange-300' 
                    : 'border-slate-200/60 hover:border-orange-300 hover:bg-orange-50 dark:border-slate-700/60 dark:hover:bg-orange-900/20'
                }`}
              >
                <Star className="h-3 w-3 mr-1" />
                Solo favoritos
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-200/60 dark:border-slate-700/60">
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filtros activos:</span>
          
          {selectedCountry && (
            <Badge variant="secondary" className="flex items-center space-x-1 bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700">
              <MapPin className="h-3 w-3" />
              <span>{selectedCountry}</span>
              <button
                onClick={() => onCountryChange("")}
                className="ml-1 hover:bg-orange-200 dark:hover:bg-orange-800/60 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {minRating && (
            <Badge variant="secondary" className="flex items-center space-x-1 bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700">
              <Star className="h-3 w-3" />
              <span>{minRating}+ estrellas</span>
              <button
                onClick={() => onRatingChange(null)}
                className="ml-1 hover:bg-orange-200 dark:hover:bg-orange-800/60 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {showFavoritesOnly && (
            <Badge variant="secondary" className="flex items-center space-x-1 bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700">
              <Star className="h-3 w-3 fill-current" />
              <span>Solo favoritos</span>
              <button
                onClick={() => onToggleFavoritesOnly(false)}
                className="ml-1 hover:bg-orange-200 dark:hover:bg-orange-800/60 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}; 